/**
 * MobilePromoBanner — "Jetzt auch als App!" Werbebanner im Kunden-Portal.
 * Wird von `/portal/mobile-promo` gesteuert (enabled, schedule, audience).
 * Trackt Views/Klicks/Dismiss via `/portal/mobile-promo/event`.
 * Auf Desktop wird zusätzlich ein QR-Code angezeigt.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone, X, Apple, PlayCircle } from 'lucide-react';
import { useCustomerAuth, API } from './PortalContext';

const isMobile = () => {
  if (typeof window === 'undefined' || !window.navigator) return false;
  return /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent);
};

const MobilePromoBanner = () => {
  const { token } = useCustomerAuth();
  const [promo, setPromo] = useState(null);
  const [visible, setVisible] = useState(true);
  const [viewLogged, setViewLogged] = useState(false);

  const logEvent = useCallback(
    async (type) => {
      if (!promo?.campaign_id || !token) return;
      try {
        await axios.post(
          `${API}/portal/mobile-promo/event`,
          { type, campaign_id: promo.campaign_id },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (e) {
        // best-effort tracking
      }
    },
    [promo?.campaign_id, token],
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/portal/mobile-promo`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.data?.active) {
          setPromo(res.data);
        }
      } catch (e) {
        // No banner available
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (promo && !viewLogged) {
      logEvent('view');
      setViewLogged(true);
    }
  }, [promo, viewLogged, logEvent]);

  if (!promo || !visible) return null;

  const handleClick = (store) => {
    const url = store === 'ios' ? promo.ios_url : promo.android_url;
    if (!url) return;
    logEvent(`click_${store}`);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDismiss = () => {
    logEvent('dismiss');
    setVisible(false);
  };

  const mobile = isMobile();
  const bg = promo.background_color || '#0B0B0F';
  const accent = promo.primary_color || '#c9a34e';

  // Prefer platform-specific URL on mobile; otherwise show both buttons + QR
  const preferredUrl =
    mobile && /iPhone|iPad|iPod/i.test(window.navigator.userAgent)
      ? promo.ios_url
      : mobile
      ? promo.android_url
      : null;

  return (
    <div
      data-testid="mobile-promo-banner"
      className="relative overflow-hidden rounded-2xl mb-4"
      style={{
        background: `linear-gradient(135deg, ${bg} 0%, #141310 100%)`,
        border: `1px solid ${accent}55`,
        boxShadow: `0 8px 32px ${accent}15`,
      }}
    >
      <button
        onClick={handleDismiss}
        data-testid="mobile-promo-dismiss"
        className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
        aria-label="Banner schliessen"
      >
        <X size={14} className="text-white/60" />
      </button>

      <div className="flex items-center gap-4 p-4 sm:p-5">
        <div
          className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ background: `${accent}22`, border: `1px solid ${accent}66` }}
        >
          <Smartphone size={22} style={{ color: accent }} />
        </div>

        <div className="flex-1 min-w-0">
          <h4 className="text-white font-bold text-sm sm:text-base">{promo.title}</h4>
          <p className="text-white/70 text-xs sm:text-sm mt-0.5 leading-snug">
            {promo.message}
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {mobile && preferredUrl ? (
              <button
                onClick={() =>
                  handleClick(
                    /iPhone|iPad|iPod/i.test(window.navigator.userAgent) ? 'ios' : 'android',
                  )
                }
                data-testid="mobile-promo-primary-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-transform active:scale-95"
                style={{ background: accent, color: '#000' }}
              >
                {promo.button_label || 'App laden'}
              </button>
            ) : (
              <>
                {promo.ios_url && (
                  <button
                    onClick={() => handleClick('ios')}
                    data-testid="mobile-promo-ios-btn"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-transform active:scale-95"
                    style={{ background: accent, color: '#000' }}
                  >
                    <Apple size={14} />
                    App Store
                  </button>
                )}
                {promo.android_url && (
                  <button
                    onClick={() => handleClick('android')}
                    data-testid="mobile-promo-android-btn"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-transform active:scale-95"
                    style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}
                  >
                    <PlayCircle size={14} />
                    Google Play
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {!mobile && promo.show_qr && (promo.ios_url || promo.android_url) && (
          <div
            className="hidden sm:flex flex-col items-center gap-1 p-2 rounded-lg"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
            data-testid="mobile-promo-qr"
          >
            <QRCodeSVG
              value={promo.ios_url || promo.android_url}
              size={72}
              bgColor="#0b0b0f"
              fgColor="#ffffff"
              level="M"
              includeMargin={false}
            />
            <span className="text-[10px] text-white/60 uppercase tracking-wider">Scannen</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobilePromoBanner;
